import React, { useState } from "react";
import Dialog from "../common/Dialog";
import FormField, {
  TextInput,
  TextArea,
  SelectInput,
} from "../common/FormField";
import RuleEditor from "./RuleEditor";
import useProtectedClose from "../../hooks/useProtectedClose";

const ENUMS = {
  scope: ["organization", "process", "category", "profile"],
  process_binding: ["fixed", "route_primary"],
  severity: ["blocking", "major", "minor"],
  stage_policy: ["none", "responsibility_intersection"],
  procedure_policy: ["standard", "technical_records", "technical_capability"],
  action: ["include_process", "include_requirement", "clarify"],
};
const REFS = {
  group_id: "group",
  category_id: "category",
  role_id: "role",
  process_id: "process",
  method_id: "method",
  sampling_id: "sampling",
  basis_id: "basis",
};
const FIELD_LABELS = {
  process_binding: "Как определить процесс",
  process_id: "Процесс",
  group_id: "Группа деятельности",
  category_id: "Категория",
  role_id: "Роль организации",
  method_id: "Метод проверки",
  sampling_id: "Правило выборки",
  basis_id: "Основание проверки",
  stage_policy: "Область этапов контроля",
  procedure_policy: "Способ проверки",
  dimension: "Измерение проверки",
  condition_id: "Условие деятельности",
};
const VALUES = {
  fixed: "Указанный процесс",
  route_primary: "Основной процесс профиля",
  organization: "Организация в целом",
  process: "Отдельный процесс",
  category: "Категория деятельности",
  profile: "Профиль деятельности",
  blocking: "Блокирующее",
  major: "Существенное",
  minor: "Незначительное",
  none: "Без ограничения по этапам",
  responsibility_intersection: "Пересечение с порученными этапами",
  standard: "Обычная проверка процесса",
  technical_records: "Проверка технических записей",
  technical_capability: "Проверка способности выполнять операцию",
  include_process: "Подключить процесс",
  include_requirement: "Подключить требование",
  clarify: "Задать вопрос для уточнения",
};

function ObjectFields({ value, onChange, label }) {
  const rows = Object.entries(value || {});
  return (
    <div className="classifier-stack">
      {rows.map(([k, v], i) => (
        <div key={i} className="classifier-inline">
          <TextInput
            aria-label={`${label}: код ${i + 1}`}
            value={k}
            onChange={(e) =>
              onChange(
                Object.fromEntries(
                  rows.map((r, j) => (j === i ? [e.target.value, r[1]] : r)),
                ),
              )
            }
          />
          <TextInput
            aria-label={`${label}: название ${i + 1}`}
            value={v}
            onChange={(e) => onChange({ ...value, [k]: e.target.value })}
          />
          <button
            type="button"
            className="ap-button ap-button--secondary"
            onClick={() =>
              onChange(Object.fromEntries(rows.filter((_, j) => j !== i)))
            }
          >
            Убрать
          </button>
        </div>
      ))}
      <button
        type="button"
        className="ap-button ap-button--secondary"
        onClick={() => onChange({ ...value, [`value-${rows.length + 1}`]: "" })}
      >
        Добавить значение
      </button>
    </div>
  );
}

export default function EntryEditor({
  entry,
  kind,
  spec,
  refs,
  onSave,
  onClose,
  busy,
  error,
}) {
  const [record, setRecord] = useState(() =>
    entry
      ? JSON.parse(JSON.stringify(entry))
      : { kind, id: "C-" + crypto.randomUUID(), status: "active", data: {} },
  );
  const [reason, setReason] = useState("");
  const [dirty, setDirty] = useState(false);
  const change = (key, value) => {
    setDirty(true);
    setRecord((r) => ({ ...r, data: { ...r.data, [key]: value } }));
  };
  const close = useProtectedClose(dirty, onClose, busy);
  const submit = (e) => {
    e.preventDefault();
    const r = { ...record, data: { ...record.data } };
    if (spec.key) r.data.id = r.id;
    if (
      kind === "attribute" &&
      !r.data.context_path &&
      ["profile", "company", "record"].includes(r.data.scope)
    )
      r.data.context_path = r.data.scope + "." + r.id;
    for (const f of spec.fields) {
      if (r.data[f.key] === undefined)
        r.data[f.key] =
          f.type === "condition"
            ? { always: true }
            : f.type === "list"
              ? []
              : null;
    }
    onSave(r, reason);
  };
  return (
    <Dialog
      label={entry ? "Изменение записи" : "Новая запись"}
      size="lg"
      onClose={close}
      closeDisabled={busy}
      className="classifier-dialog"
    >
      <div className="classifier-dialog-header">
        <h2 data-dialog-heading tabIndex={-1}>
          {entry ? "Изменить запись" : "Добавить запись"} · {spec.sheet}
        </h2>
        <button
          type="button"
          className="ap-button ap-button--secondary"
          onClick={close}
          disabled={busy}
        >
          Закрыть
        </button>
      </div>
      <form onSubmit={submit} className="classifier-stack">
        <fieldset disabled={busy} className="classifier-stack">
          {error && (
            <p role="alert" className="classifier-error">
              {error}
            </p>
          )}
          <div className="classifier-form-grid">
            <FormField
              label="Постоянный идентификатор"
              hint="Не меняется после создания. Название можно редактировать."
            >
              <TextInput
                value={record.id}
                readOnly={!!entry}
                onChange={(e) => {
                  setDirty(true);
                  setRecord({ ...record, id: e.target.value });
                }}
                required
              />
            </FormField>
            <FormField label="Использование">
              <SelectInput
                value={record.status}
                onChange={(e) => {
                  setDirty(true);
                  setRecord({ ...record, status: e.target.value });
                }}
              >
                <option value="active">Используется</option>
                <option value="inactive">Выведена из использования</option>
              </SelectInput>
            </FormField>
          </div>
          {spec.fields
            .filter((f) => f.key !== "id")
            .map((f) => {
              const label = FIELD_LABELS[f.key] || f.label,
                value = record.data[f.key],
                options =
                  kind === "rule" && f.key === "target_id"
                    ? refs.choices[
                        record.data.action === "include_requirement"
                          ? "requirement"
                          : "process"
                      ]
                    : REFS[f.key]
                      ? refs.choices[REFS[f.key]]
                      : null;
              if (f.type === "condition")
                return (
                  <RuleEditor
                    key={f.key}
                    value={value || { always: true }}
                    onChange={(v) => change(f.key, v)}
                    {...refs}
                  />
                );
              if (f.type === "object")
                return (
                  <section key={f.key}>
                    <h3>{f.label.replace(" JSON", "")}</h3>
                    <ObjectFields
                      label={f.label}
                      value={value}
                      onChange={(v) => change(f.key, v)}
                    />
                  </section>
                );
              if (f.type === "list")
                return (
                  <FormField
                    key={f.key}
                    label={f.label.replace(" JSON", "")}
                    hint="Каждое значение с новой строки. Идентификаторы сохраняйте точно."
                  >
                    <TextArea
                      rows={3}
                      value={(value || []).join("\n")}
                      onChange={(e) =>
                        change(
                          f.key,
                          e.target.value.split("\n").filter(Boolean),
                        )
                      }
                    />
                  </FormField>
                );
              const enumValues =
                kind === "requirement"
                  ? ENUMS[f.key]
                  : kind === "rule" && f.key === "action"
                    ? ENUMS.action
                    : kind === "attribute" && f.key === "scope"
                      ? ["company", "profile", "record"]
                      : kind === "attribute" && f.key === "value_type"
                        ? [
                            ...new Set([
                              "yes/no/unknown",
                              "string",
                              "number",
                              "integer",
                              "enum",
                              "set",
                              ...(refs.choices.attribute || []).map(
                                (a) => a.data.value_type,
                              ),
                            ]),
                          ]
                        : null;
              if (options || enumValues)
                return (
                  <FormField key={f.key} label={label}>
                    <SelectInput
                      value={value || ""}
                      onChange={(e) => change(f.key, e.target.value || null)}
                    >
                      <option value="">Не задано</option>
                      {options?.map((o) => (
                        <option key={o.id} value={o.id}>
                          {o.title} ({o.id})
                        </option>
                      ))}
                      {enumValues?.map((o) => (
                        <option key={o} value={o}>
                          {VALUES[o] || o}
                        </option>
                      ))}
                    </SelectInput>
                  </FormField>
                );
              return (
                <FormField key={f.key} label={label}>
                  {[
                    "title",
                    "field",
                    "context_path",
                    "input_path",
                    "value_type",
                    "url",
                  ].includes(f.key) ? (
                    <TextInput
                      value={value || ""}
                      onChange={(e) => change(f.key, e.target.value || null)}
                    />
                  ) : (
                    <TextArea
                      rows={3}
                      value={value || ""}
                      onChange={(e) => change(f.key, e.target.value || null)}
                    />
                  )}
                </FormField>
              );
            })}
          <FormField label="Причина изменения" required>
            <TextArea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              rows={2}
            />
          </FormField>
          <p className="classifier-muted">
            Изменяется только черновик библиотеки. Существующие аудиты не
            пересчитываются. Связанные ошибки будут показаны перед публикацией.
          </p>
        </fieldset>
        <div className="classifier-dialog-footer">
          <button
            className="ap-button ap-button--primary"
            disabled={busy}
            type="submit"
          >
            {busy ? "Сохраняем…" : "Сохранить"}
          </button>
          <button
            className="ap-button ap-button--secondary"
            type="button"
            disabled={busy}
            onClick={close}
          >
            Отмена
          </button>
        </div>
      </form>
    </Dialog>
  );
}
